import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useChatStore } from '../../store/chatStore';
import glassTheme from '../../styles/glassTheme';

const SidebarContainer = styled.div<{ $isOpen: boolean }>`
  width: ${props => props.$isOpen ? '280px' : '0'};
  height: 100%;
  min-height: 0;
  background: rgba(0, 0, 0, 0.9);
  border-right: 1px solid ${glassTheme.colors.border};
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;

  @media (min-width: 769px) and (max-width: 1024px) {
    width: ${props => props.$isOpen ? '240px' : '0'};
  }

  @media (max-width: 768px) {
    position: absolute;
    inset: 0 auto 0 0;
    width: ${props => props.$isOpen ? 'min(85vw, 320px)' : '0'};
    z-index: 5;
  }
`;

const SidebarHeader = styled.div`
  height: 56px;
  min-height: 56px;
  padding: 0 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const CollapseButton = styled.button`
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const NewChatButton = styled.button`
  width: 100%;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const ConversationSection = styled.div`
  margin-bottom: 1rem;
`;

const SectionHeader = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ConversationItem = styled.div<{ $active: boolean }>`
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.25rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  position: relative;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const ConversationIcon = styled.span`
  font-size: 0.9rem;
  opacity: 0.7;
  flex-shrink: 0;
`;

const ConversationTitleWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ConversationTitle = styled.div`
  flex: 1;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditInput = styled.input`
  flex: 1;
  font-size: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.5);
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  color: white;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
  position: relative;
  z-index: 100;
`;

const EditButton = styled.button<{ $variant?: 'save' | 'cancel' }>`
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.65rem;
  transition: all 0.2s ease;
  background: ${props => props.$variant === 'save' ? 'rgba(16, 163, 127, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  color: ${props => props.$variant === 'save' ? '#10a37f' : '#ef4444'};
  position: relative;
  z-index: 101;

  &:hover {
    background: ${props => props.$variant === 'save' ? 'rgba(16, 163, 127, 0.5)' : 'rgba(239, 68, 68, 0.5)'};
  }
`;

const ConversationActions = styled.div<{ $visible: boolean; $editing: boolean }>`
  display: ${props => props.$editing ? 'none' : (props.$visible ? 'flex' : 'none')};
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ConversationItem}:hover &, ${ConversationItem}:focus-within & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: ${glassTheme.colors.text.secondary};
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &.delete:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface ChatSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ isOpen, onToggle }) => {
  const {
    conversations,
    currentConversation,
    isLoadingConversations,
    loadConversations,
    createNewConversation,
    loadConversation,
    deleteConversation,
    updateConversationTitle
  } = useChatStore();

  const [hoveredConversation, setHoveredConversation] = useState<string | null>(null);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen, loadConversations]);

  useEffect(() => {
    if (editingConversationId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingConversationId]);

  const handleNewChat = () => {
    createNewConversation();
  };

  const handleSelectConversation = (conversationId: string) => {
    if (editingConversationId) return;
    loadConversation(conversationId);
  };

  const handleDeleteConversation = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (window.confirm('确定要删除这个对话吗？')) {
      deleteConversation(conversationId);
    }
  };

  const handleStartEdit = (e: React.MouseEvent, conversationId: string, currentTitle: string) => {
    e.stopPropagation();
    setEditingConversationId(conversationId);
    setEditTitle(currentTitle);
  };

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditTitle('');
  };

  const handleSaveEdit = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (editingConversationId && editTitle.trim()) {
      const success = await updateConversationTitle(editingConversationId, editTitle.trim());
      if (success) {
        setEditingConversationId(null);
        setEditTitle('');
      }
    } else {
      setEditingConversationId(null);
      setEditTitle('');
    }
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Group conversations by date
  const groupConversations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups: { [key: string]: typeof conversations } = {
      today: [],
      yesterday: [],
      last7Days: [],
      older: []
    };

    conversations.forEach(conv => {
      const convDate = new Date(conv.createdAt);
      if (convDate >= today) {
        groups.today.push(conv);
      } else if (convDate >= yesterday) {
        groups.yesterday.push(conv);
      } else if (convDate >= lastWeek) {
        groups.last7Days.push(conv);
      } else {
        groups.older.push(conv);
      }
    });

    return groups;
  };

  const groups = groupConversations();

  const renderConversationItem = (conv: any) => {
    const isEditing = editingConversationId === conv._id;
    const isActive = currentConversation?._id === conv._id;
    const isHovered = hoveredConversation === conv._id;

    return (
      <ConversationItem
        key={conv._id}
        $active={isActive}
        onClick={() => handleSelectConversation(conv._id)}
        onMouseEnter={() => setHoveredConversation(conv._id)}
        onMouseLeave={() => setHoveredConversation(null)}
        role="option"
        aria-selected={isActive}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSelectConversation(conv._id);
          }
        }}
      >
        <ConversationIcon>💬</ConversationIcon>
        <ConversationTitleWrapper>
          {isEditing ? (
            <>
              <EditInput
                ref={editInputRef}
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleEditKeyPress}
                onClick={(e) => e.stopPropagation()}
              />
              <EditActions>
                <EditButton aria-label="保存标题" $variant="save" onClick={(e) => handleSaveEdit(e)}>
                  <i className="fas fa-check"></i>
                </EditButton>
                <EditButton aria-label="取消编辑" $variant="cancel" onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEdit();
                }}>
                  <i className="fas fa-times"></i>
                </EditButton>
              </EditActions>
            </>
          ) : (
            <>
              <ConversationTitle>{conv.title}</ConversationTitle>
              <ConversationActions $visible={isHovered} $editing={false}>
                <ActionButton
                  aria-label="编辑对话标题"
                  onClick={(e) => handleStartEdit(e, conv._id, conv.title)}
                >
                  <i className="fas fa-edit"></i>
                </ActionButton>
                <ActionButton
                  aria-label="删除对话"
                  className="delete"
                  onClick={(e) => handleDeleteConversation(e, conv._id)}
                >
                  <i className="fas fa-trash"></i>
                </ActionButton>
              </ConversationActions>
            </>
          )}
        </ConversationTitleWrapper>
      </ConversationItem>
    );
  };

  if (!isOpen) return null;

  return (
    <SidebarContainer $isOpen={isOpen}>
      <SidebarHeader>
        <NewChatButton onClick={handleNewChat}>
          <i className="fas fa-plus"></i>
          新对话
        </NewChatButton>
        <CollapseButton onClick={onToggle} aria-label="收起对话记录">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="currentColor" fillRule="evenodd" d="M21 5H11v14h10zM3 5h6v14H3zm0-2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm2 4.25a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5zm-.75 3.5A.75.75 0 0 1 5 10h2a.75.75 0 0 1 0 1.5H5a.75.75 0 0 1-.75-.75m.75 2a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5z" clipRule="evenodd"></path>
          </svg>
        </CollapseButton>
      </SidebarHeader>

      <SidebarContent>
        {isLoadingConversations ? (
          <LoadingSpinner>
            <Spinner aria-hidden="true" />
            <span>正在加载对话…</span>
          </LoadingSpinner>
        ) : conversations.length === 0 ? (
          <EmptyState>
            <i className="fas fa-comments" style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }}></i>
            暂无对话记录
            <div>点击“新对话”开始提问</div>
          </EmptyState>
        ) : (
          <>
            {groups.today.length > 0 && (
              <ConversationSection>
                <SectionHeader>今天</SectionHeader>
                {groups.today.map(renderConversationItem)}
              </ConversationSection>
            )}

            {groups.yesterday.length > 0 && (
              <ConversationSection>
                <SectionHeader>昨天</SectionHeader>
                {groups.yesterday.map(renderConversationItem)}
              </ConversationSection>
            )}

            {groups.last7Days.length > 0 && (
              <ConversationSection>
                <SectionHeader>过去7天</SectionHeader>
                {groups.last7Days.map(renderConversationItem)}
              </ConversationSection>
            )}

            {groups.older.length > 0 && (
              <ConversationSection>
                <SectionHeader>更早</SectionHeader>
                {groups.older.map(renderConversationItem)}
              </ConversationSection>
            )}
          </>
        )}
      </SidebarContent>
    </SidebarContainer>
  );
};

export default ChatSidebar;
